import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

interface ShellToolbarContextValue {
  center: ReactNode;
  setCenter: (content: ReactNode) => void;
  leadingActions: ReactNode;
  setLeadingActions: (content: ReactNode) => void;
}

const ShellToolbarContext = createContext<ShellToolbarContextValue | null>(null);

export function ShellToolbarProvider({ children }: { children: ReactNode }) {
  const [center, setCenter] = useState<ReactNode>(null);
  const [leadingActions, setLeadingActions] = useState<ReactNode>(null);
  const value = useMemo(
    () => ({ center, setCenter, leadingActions, setLeadingActions }),
    [center, leadingActions]
  );
  return <ShellToolbarContext.Provider value={value}>{children}</ShellToolbarContext.Provider>;
}

export function useShellToolbarCenter(content: ReactNode | null) {
  const ctx = useContext(ShellToolbarContext);
  if (!ctx) return;

  const { setCenter } = ctx;
  useEffect(() => {
    setCenter(content);
  }, [content, setCenter]);

  useEffect(() => {
    return () => setCenter(null);
  }, [setCenter]);
}

export function useShellToolbarCenterContent(): ReactNode {
  return useContext(ShellToolbarContext)?.center ?? null;
}

export function useShellToolbarLeadingActions(content: ReactNode | null) {
  const ctx = useContext(ShellToolbarContext);
  if (!ctx) return;

  const { setLeadingActions } = ctx;
  useEffect(() => {
    setLeadingActions(content);
  }, [content, setLeadingActions]);

  useEffect(() => {
    return () => setLeadingActions(null);
  }, [setLeadingActions]);
}

export function useShellToolbarLeadingActionsContent(): ReactNode {
  return useContext(ShellToolbarContext)?.leadingActions ?? null;
}
